/**
 * Apply an earned offer to Shopify's cart. Shopify owns eligibility, stacking,
 * prices and persistence; this module never calculates a percentage discount.
 * https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
 */

export function normalizeDiscountCode(value) {
  if (typeof value !== 'string') return '';
  const code = value.trim();
  return /^[a-z0-9_-]{1,100}$/i.test(code) ? code : '';
}

export function discountState(cart, code) {
  const match = cart.discount_codes?.find((entry) => entry.code?.toUpperCase() === code.toUpperCase());
  if (match?.applicable === true) return 'applied';
  if (match && cart.item_count === 0) return 'saved';
  if (match?.applicable === false) return 'ineligible';
  return 'unconfirmed';
}

export function discountMessage(state) {
  const messages = {
    applying: 'Applying your offer to your cart…',
    applied: 'Your offer is applied. Your cart shows the eligible savings.',
    'applied-unrefreshed': 'Your offer is applied. Refresh your cart to see the updated prices.',
    saved: 'Your offer is saved. Add eligible items to see your discount.',
    ineligible: 'This offer does not apply to your current cart. Checkout confirms eligibility.',
    unconfirmed: 'Shopify has not applied this offer. You can try the code at checkout.',
    error: 'We could not apply the offer automatically. Use the code at checkout.',
  };
  return messages[state] || '';
}

/** Dependency injection keeps real request, stacking and failure behavior testable. */
export function createDiscountApplier({ request, root = '/', sectionIds = () => [], publish = () => {}, beforeRead = async () => {}, revision = () => 0 }) {
  const route = /^\/(?!\/)[^?#]*\/$/.test(root) ? root : '/';
  const pending = new Map();
  let queue = Promise.resolve();

  async function readResponse(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await request(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Shopify cart request failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) {
        throw new Error('Shopify returned an invalid cart');
      }
      return cart;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function apply(code) {
    let current;
    for (let attempt = 0; attempt < 3; attempt++) {
      await beforeRead();
      const readRevision = revision();
      const snapshot = await readResponse(`${route}cart.js`);
      if (readRevision === revision()) {
        current = snapshot;
        break;
      }
    }
    if (!current) throw new Error('Cart discounts changed while applying the offer');
    const codes = [];
    const seen = new Set();
    for (const candidate of [...(current.discount_codes || []).map((entry) => entry.code), code]) {
      // Preserve Shopify-returned existing codes, including codes whose eligibility
      // depends on later cart contents. A new offer must not silently erase them.
      if (typeof candidate !== 'string' || !candidate || candidate.includes(',')) continue;
      const key = candidate.toUpperCase();
      if (!seen.has(key)) {
        seen.add(key);
        codes.push(candidate);
      }
    }

    const sections = [...new Set(sectionIds().filter(Boolean))].slice(0, 5);
    const cart = await readResponse(`${route}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), ...(sections.length ? { sections } : {}) }),
    });
    // The response is authoritative even if a section failed to render. The native
    // cart event listener requests fresh section HTML when it is absent or null.
    let refreshed = true;
    try {
      await publish(cart, codes);
    } catch {
      // A rendering/event failure cannot undo a successful Shopify mutation.
      refreshed = false;
    }
    return { state: discountState(cart, code), cart, refreshed };
  }

  return function applyCode(value) {
    const code = normalizeDiscountCode(value);
    if (!code) return Promise.reject(new Error('Invalid offer code'));
    const key = code.toUpperCase();
    if (pending.has(key)) return pending.get(key);
    // Serializing different earned codes avoids two read/merge/write operations
    // replacing each other's discount list. Repeated notifications share one job.
    const job = queue.then(() => apply(code));
    pending.set(key, job);
    queue = job.catch(() => {});
    job.finally(() => pending.delete(key)).catch(() => {});
    return job;
  };
}

if (typeof document !== 'undefined' && document.body?.dataset.prodaSurface === 'consumer') {
  let latestState = '';
  let latestCode = '';
  let cartEventRevision = 0;
  let nativeDiscountRevision = 0;
  let activeApplications = 0;
  const pendingNativeDiscounts = new Set();
  const ownDiscountEvents = new WeakSet();
  const lockedControls = new Set();
  const lockControls = () => {
    document.querySelectorAll('cart-discount-component input, cart-discount-component button').forEach((element) => {
      if (!element.disabled) {
        element.disabled = true;
        lockedControls.add(element);
      }
    });
  };
  const lockObserver = new MutationObserver(() => { if (activeApplications) lockControls(); });
  const beginApplication = () => {
    if (activeApplications++ !== 0) return;
    lockControls();
    lockObserver.observe(document.body, { childList: true, subtree: true });
  };
  const endApplication = () => {
    if (--activeApplications !== 0) return;
    lockObserver.disconnect();
    for (const element of lockedControls) element.disabled = false;
    lockedControls.clear();
  };
  const preventConflictingInteraction = (event) => {
    if (!activeApplications || !(event.target instanceof Element)) return;
    if (event.target.closest('cart-discount-component, [data-proda-discount-redemption]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
  document.addEventListener('submit', preventConflictingInteraction, true);
  document.addEventListener('click', preventConflictingInteraction, true);
  const refreshStatus = () => {
    if (!latestState) return;
    document.dispatchEvent(new CustomEvent('proda:discount-state', { detail: { code: latestCode, state: latestState } }));
    document.querySelectorAll('[data-proda-discount-status]').forEach((element) => {
      element.textContent = discountMessage(latestState);
      element.dataset.prodaDiscountState = latestState;
    });
    document.querySelectorAll('[data-proda-discount-redemption]').forEach((element) => {
      const fallback = element.dataset.prodaDiscountFallback || element.getAttribute('href');
      if (!fallback) return;
      element.dataset.prodaDiscountFallback = fallback;
      element.setAttribute('href', fallback);
      if (latestState === 'applying') element.setAttribute('aria-disabled', 'true');
      else element.removeAttribute('aria-disabled');
      if (!['applied', 'applied-unrefreshed', 'saved'].includes(latestState)) return;
      // Once Shopify has the code, shop directly. Visiting /discount/CODE again
      // could replace another code that was deliberately preserved in the cart.
      try {
        const url = new URL(fallback, window.location.origin);
        const redirect = url.searchParams.get('redirect');
        if (url.origin !== window.location.origin || !redirect?.startsWith('/') || redirect.startsWith('//')) return;
        const destination = new URL(redirect, window.location.origin);
        if (destination.origin === window.location.origin) {
          element.setAttribute('href', destination.pathname + destination.search + destination.hash);
        }
      } catch { /* Keep the existing manual redemption link. */ }
    });
  };
  const applyCode = createDiscountApplier({
    request: window.fetch.bind(window),
    root: window.Shopify?.routes?.root || '/',
    sectionIds: () => Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId),
    beforeRead: async () => {
      let timer;
      try {
        await Promise.race([
          Promise.allSettled([...pendingNativeDiscounts]),
          new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Cart update still pending')), 10000); }),
        ]);
      } finally { clearTimeout(timer); }
    },
    revision: () => nativeDiscountRevision,
    publish: async (cart, codes) => {
      const { CartDiscountUpdateEvent } = await import('@shopify/events');
      const deferred = CartDiscountUpdateEvent.createPromise();
      const event = new CartDiscountUpdateEvent({
        discountCodes: codes.map((code) => ({ code })),
        promise: deferred.promise,
      });
      ownDiscountEvents.add(event);
      document.dispatchEvent(event);
      deferred.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections, items: cart.items, source: 'proda-earned-offer' },
      });
    },
  });

  document.addEventListener('proda:discount-status-needed', refreshStatus);
  for (const eventName of ['shopify:cart:lines-update', 'shopify:cart:discount-update']) {
    document.addEventListener(eventName, (event) => {
      if (eventName === 'shopify:cart:discount-update' && event.promise && !ownDiscountEvents.has(event)) {
        nativeDiscountRevision++;
        pendingNativeDiscounts.add(event.promise);
        event.promise.finally(() => pendingNativeDiscounts.delete(event.promise)).catch(() => {});
      }
      const code = latestCode;
      if (!code || !event.promise) return;
      const revision = ++cartEventRevision;
      event.promise.then(({ cart }) => {
        // Respect later removals and changing cart eligibility; never reapply an
        // offer merely because the shopper explicitly removed it.
        if (revision !== cartEventRevision || latestCode !== code || latestState === 'applying' || !Array.isArray(cart?.discountCodes)) return;
        latestState = discountState({ discount_codes: cart.discountCodes, item_count: cart.totalQuantity }, code);
        refreshStatus();
      }).catch(() => {});
    });
  }
  document.addEventListener('proda:discount-issued', async (event) => {
    // Preview form advances must never create a real discount or enrollment.
    if (document.body.dataset.storefrontEnvironment !== 'production') return;
    const code = normalizeDiscountCode(event.detail?.code);
    if (!['WELCOME15', 'PRODADM15', 'SCRATCH25', 'SCRATCH'].includes(code.toUpperCase())) return;
    latestCode = code;
    latestState = 'applying';
    beginApplication();
    refreshStatus();
    try {
      const result = await applyCode(code);
      if (latestCode === code) latestState = result.state === 'applied' && !result.refreshed ? 'applied-unrefreshed' : result.state;
    } catch {
      if (latestCode === code) latestState = 'error';
    } finally {
      endApplication();
    }
    refreshStatus();
  });
}
